import React, { useRef } from "react";
import { FileText, ImageIcon, Link2, X } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const MultiFileUpload = ({ files, setFiles, multiple = true, accept }) => {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    const formattedFiles = selectedFiles.map((file) => ({
      file,
    }));

    if (multiple) {
      setFiles((prev) => prev ? [...prev, ...formattedFiles] : [...formattedFiles]);
    } else {
      setFiles([...formattedFiles]);
    }

    e.target.value = "";
  };

  const removeFile = (index) => {
    if (multiple) {
      setFiles((prev) => prev.filter((_, i) => i !== index));
    } else {
      setFiles([]);
    }
  };

  const formatSize = (size) => {
    if (!size) return "";
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getPreviewIcon = (item) => {
    // URL-based file
    if (item.url) {
      return <Link2 className="h-4 w-4 text-blue-600" />;
    }

    // Binary File
    if (item.file) {
      if (item.file.type.startsWith("image/")) {
        return <ImageIcon className="h-4 w-4 text-green-600" />;
      }
      return <FileText className="h-4 w-4 text-muted-foreground" />;
    }

    return null;
  };

  const getFileName = (item) => item.name || item.file?.name;
  const getFileSize = (item) => item.file?.size;

  return (
    <div className="space-y-3 max-w-full">
      <div className="flex items-center gap-2">
        <Input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          className="hidden"
          onChange={handleFileChange}
          {...(accept ? { accept } : {})} // ✅ DEFAULT ALL
        />

        <Button
          variant="outline"
          onClick={(e) => {
            e.preventDefault();
            fileInputRef.current.click();
          }}
        >
          Choose {multiple ? "Files" : "File"}
        </Button>

        {files?.length > 0 && (
          <span className="text-sm text-muted-foreground">
            {files?.length} files
          </span>
        )}
      </div>

      {/* File List */}
      <div className="space-y-2">
        {files?.map((item, index) => (
          <div
            key={index}
            className={
              "flex items-center justify-between rounded-md border p-2"
            }
          >
            <div className="flex items-center gap-2">
              {getPreviewIcon(item)}

              <span
                className={
                  item?.url
                    ? "text-sm truncate max-w-[220px] hover:cursor-pointer hover:underline"
                    : "text-sm truncate max-w-[220px]"
                }
                onClick={() => {
                  if (item.url) {
                    window.open(item.url, "_blank", "noopener,noreferrer");
                  }
                }}
              >
                {getFileName(item)}
              </span>

              {item.file && (
                <span className="text-xs text-muted-foreground">
                  ({formatSize(getFileSize(item))})
                </span>
              )}
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.preventDefault();
                removeFile(index)
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MultiFileUpload;
